import { minimalTimezoneSet } from 'constants/common'

const TIMEZONE_OFFSET_FALLBACK = '+00:00'

function isValidIanaTimeZone(timeZoneId: string): boolean {
  if (!timeZoneId?.trim()) {
    return false
  }

  try {
    const formatter = new Intl.DateTimeFormat('en-US', { timeZone: timeZoneId })
    formatter.format(new Date())
    return true
  } catch {
    return false
  }
}

export function getTimezoneOffset(
  timeZoneId: string,
  date: Date = new Date()
): string {
  if (!isValidIanaTimeZone(timeZoneId)) {
    return TIMEZONE_OFFSET_FALLBACK
  }

  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZoneId,
      timeZoneName: 'longOffset' as Intl.DateTimeFormatOptions['timeZoneName'],
    })
    const offsetPart = formatter
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')

    const offset = offsetPart?.value?.replace('GMT', '').trim()
    return offset || TIMEZONE_OFFSET_FALLBACK
  } catch {
    return TIMEZONE_OFFSET_FALLBACK
  }
}

export function getBrowserTimeZoneAbbr(date: Date = new Date()): string {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZoneName: 'short',
  })

  const parts = formatter.formatToParts(date)
  const abbr = parts.find((p) => p.type === 'timeZoneName')?.value

  return abbr ?? ''
}

export function getBrowserTimeZoneFull(date: Date = new Date()): string {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZoneName: 'long',
  })

  const parts = formatter.formatToParts(date)
  const tz = parts.find((p) => p.type === 'timeZoneName')?.value

  return tz ?? ''
}

export function getIanaTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

export function resolveTimezoneByTzCode(
  tzCode: string,
  tzName?: string
): TimeZone {
  const normalizedTzCode = tzCode?.trim()
  const normalizedName = tzName?.trim().toLowerCase()

  if (!isValidIanaTimeZone(normalizedTzCode)) {
    const fallbackByName = normalizedName
      ? minimalTimezoneSet.find(
          (tz) => tz.tzName.toLowerCase() === normalizedName
        )
      : undefined

    return fallbackByName ?? minimalTimezoneSet[0]
  }

  const matchedTimezones = minimalTimezoneSet.filter(
    (tz) => tz.tzCode.toLowerCase() === normalizedTzCode.toLowerCase()
  )

  if (matchedTimezones.length > 0) {
    if (normalizedName) {
      const byName = matchedTimezones.find(
        (tz) => tz.tzName.toLowerCase() === normalizedName
      )

      if (byName) {
        return byName
      }
    }

    const currentOffset = getTimezoneOffset(normalizedTzCode)
    const byOffset = matchedTimezones.find((tz) => tz.offset === currentOffset)
    return byOffset ?? matchedTimezones[0]
  }

  const offset = getTimezoneOffset(normalizedTzCode)
  const safeTzName = tzName?.trim() || normalizedTzCode

  return {
    tzCode: normalizedTzCode,
    tzAbbr:
      safeTzName
        .split(/\s+/)
        .map((word) => word[0])
        .join('') || 'TZ',
    tzName: safeTzName,
    offset,
    label: `(GMT${offset}) ${safeTzName}`,
  }
}
